import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { VIEW_MODES } from "../../hooks/useViewPreference";
import ViewToggle from "./ViewToggle";
import CardGridView from "./CardGridView";
import ActivityStreamView from "./ActivityStreamView";
import TableView from "./TableView";
import {
  brandButton,
  darkSurface,
  heroText,
  motionVariants,
  pageBackground,
} from "../../helpers/designSystem";

const viewTransitionVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

/**
 * Main unified dashboard component.
 *
 * Aesthetic: matches the public showcase / wizard surfaces — dark
 * gradient page background (or warm-white in light mode), a hero
 * "Your apps" band with brand-orange accent, glass toolbar, and
 * stagger-fade entrance on the grid. All visuals route through
 * helpers/designSystem so subsequent pages (Domains, Configs, Costs)
 * pick up the same tokens with one import.
 */
export function UnifiedDashboard({
  apps,
  loading,
  viewMode,
  onViewModeChange,
  onRefresh,
  onAddApp,
  onOpenPipeline,
  onRestart,
  onRebuild,
  onRename,
  onDelete,
  onManageDomains,
  onCardClick,
  boundServicesByApp,
  onOpenBoundService,
  showAddButton = true,
  addButtonDisabled = false,
  // Rendered as a small helper line under "Your apps" when the Add
  // Application button is hidden — tells the user exactly which
  // permission to ask for and who can grant it. Pass null (or omit) to
  // hide the helper (e.g., admins with the button visible).
  addButtonHiddenReason = null,
  // Optional secondary actions rendered to the LEFT of Add Application
  // inside the hero band. Each: { label, icon, onClick, disabled,
  // component?, render?: ()=>ReactNode }. `render` wins if both are
  // set (lets ManageApps inject MUI <Button component="label"> with
  // a hidden <input type="file">).
  extraHeroActions = [],
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Search state
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter apps by search
  const filteredApps = useMemo(() => {
    if (!searchValue.trim()) return apps;

    const search = searchValue.toLowerCase();
    return apps.filter(
      (app) =>
        app.displayName?.toLowerCase().includes(search) ||
        app.name?.toLowerCase().includes(search) ||
        app.build?.gitUrl?.toLowerCase().includes(search)
    );
  }, [apps, searchValue]);

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setPage(0);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const appsCount = Array.isArray(apps) ? apps.length : 0;

  return (
    <Box
      sx={{
        ...pageBackground(isDarkMode),
        // Negative margin so the page bg paints edge-to-edge inside
        // the existing MainLayout content padding; clip to viewport so
        // the gradient doesn't bleed past the scroll area.
        mx: -3,
        mt: -3,
        px: 3,
        pt: 3,
        pb: 4,
        borderRadius: 0,
      }}
    >
      {/* Hero band */}
      <Box
        component={motion.div}
        initial="initial"
        animate="animate"
        variants={motionVariants.heroEnter}
        sx={{
          mb: 4,
          display: "flex",
          alignItems: { xs: "flex-start", md: "flex-end" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              ...heroText(isDarkMode),
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              mb: 0.5,
            }}
          >
            Your apps
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? darkSurface.textMuted : "#475569",
              fontSize: "0.95rem",
            }}
          >
            {appsCount === 0
              ? "Spin up your first app from a starter — deployed in 3 minutes."
              : `${appsCount} app${appsCount === 1 ? "" : "s"} in this project. Auto-scaled, auto-built, auto-deployed.`}
          </Typography>
          {/* Explains WHY the "+ Add Application" CTA is missing when
              the caller doesn't hold the create-apps grant. Only
              rendered when the button is hidden AND we have a reason
              string to show; admins with the live button see nothing. */}
          {!showAddButton && addButtonHiddenReason && (
            <Typography
              data-testid="add-app-hidden-reason"
              variant="body2"
              sx={{
                mt: 1,
                color: isDarkMode ? darkSurface.textFaint : "#64748b",
                fontSize: "0.85rem",
                fontStyle: "italic",
              }}
            >
              {addButtonHiddenReason}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          {/* Secondary actions first (left of the primary CTA) */}
          {extraHeroActions.map((action, idx) => {
            if (action.render) return <React.Fragment key={idx}>{action.render()}</React.Fragment>;
            return (
              <Button
                key={action.label || idx}
                variant="outlined"
                startIcon={action.icon}
                onClick={action.onClick}
                disabled={action.disabled}
                component={action.component || "button"}
                tabIndex={action.component === "label" ? -1 : undefined}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "10px",
                  px: 2,
                  height: 44,
                  borderColor: isDarkMode
                    ? "rgba(253,186,116,0.35)"
                    : "rgba(234,88,12,0.35)",
                  color: isDarkMode ? "#fdba74" : "#9a3412",
                  background: isDarkMode
                    ? "rgba(234,88,12,0.06)"
                    : "rgba(234,88,12,0.04)",
                  "&:hover": {
                    borderColor: isDarkMode
                      ? "rgba(253,186,116,0.6)"
                      : "rgba(234,88,12,0.6)",
                    background: isDarkMode
                      ? "rgba(234,88,12,0.12)"
                      : "rgba(234,88,12,0.08)",
                  },
                }}
              >
                {action.label}
                {action.children}
              </Button>
            );
          })}
          {showAddButton && (
            <Button
              id="ADD"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddApp}
              disabled={addButtonDisabled}
              sx={{ ...brandButton(), height: 44, px: 3 }}
            >
              Add Application
            </Button>
          )}
        </Box>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
          p: 1.25,
          pl: 2,
          borderRadius: 2,
          background: isDarkMode
            ? "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)"
            : "rgba(255,255,255,0.7)",
          border: `1px solid ${isDarkMode ? darkSurface.border : "rgba(15,23,42,0.06)"}`,
          backdropFilter: "blur(12px)",
        }}
      >
        <TextField
          size="small"
          placeholder="Search apps..."
          value={searchValue}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ color: isDarkMode ? darkSurface.textFaint : "#94a3b8", fontSize: "1.2rem" }}
                />
              </InputAdornment>
            ),
            endAdornment: searchValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon sx={{ fontSize: "1rem", color: isDarkMode ? darkSurface.textFaint : "#64748b" }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: 280 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              background: isDarkMode ? "rgba(0,0,0,0.2)" : "#f8fafc",
              color: isDarkMode ? darkSurface.text : "#0f172a",
              "& fieldset": {
                borderColor: isDarkMode ? darkSurface.border : "#e2e8f0",
              },
              "&:hover fieldset": {
                borderColor: isDarkMode ? darkSurface.borderStrong : "#cbd5e1",
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: isDarkMode ? darkSurface.textFaint : "#94a3b8",
              opacity: 1,
            },
          }}
        />

        <Box sx={{ flex: 1 }} />

        <ViewToggle value={viewMode} onChange={onViewModeChange} />

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: isDarkMode ? darkSurface.border : "#e2e8f0",
            color: isDarkMode ? darkSurface.textMuted : "#64748b",
            "&:hover": {
              borderColor: isDarkMode ? "rgba(253,186,116,0.35)" : "rgba(234,88,12,0.25)",
              background: isDarkMode ? "rgba(234,88,12,0.06)" : "rgba(234,88,12,0.04)",
              color: isDarkMode ? "#fdba74" : "#9a3412",
            },
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Content */}
      <AnimatePresence mode="wait">
        <Box
          key={viewMode}
          component={motion.div}
          variants={viewTransitionVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
        >
          {viewMode === VIEW_MODES.CARD && (
            <CardGridView
              apps={filteredApps}
              loading={loading}
              onOpenPipeline={onOpenPipeline}
              onRestart={onRestart}
              onRebuild={onRebuild}
              onRename={onRename}
              onDelete={onDelete}
              onManageDomains={onManageDomains}
              onCardClick={onCardClick}
              boundServicesByApp={boundServicesByApp}
              onOpenBoundService={onOpenBoundService}
            />
          )}

          {viewMode === VIEW_MODES.ACTIVITY && (
            <ActivityStreamView
              apps={filteredApps}
              loading={loading}
              onOpenPipeline={onOpenPipeline}
              onRestart={onRestart}
              onRebuild={onRebuild}
              onRename={onRename}
              onDelete={onDelete}
              onManageDomains={onManageDomains}
              onCardClick={onCardClick}
            />
          )}

          {viewMode === VIEW_MODES.TABLE && (
            <TableView
              apps={filteredApps}
              loading={loading}
              page={page}
              rowsPerPage={rowsPerPage}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              onRowClick={(app) => onCardClick?.(app)}
              searchedValue={searchValue}
              onChangeSearchTxt={handleSearchChange}
              onKeyPressSearch={() => {}}
              onClearSearchTxt={handleClearSearch}
            />
          )}
        </Box>
      </AnimatePresence>
    </Box>
  );
}

export default UnifiedDashboard;
